import React, { useState, useEffect } from 'react';
import { Form, Alert, Button as BsButton } from "react-bootstrap";
import { PageTitle } from "./Controls/PageTitle";
import { Select } from "./Controls/Select";
import { useForm } from "react-hook-form";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { MediumPage } from "./Controls/MediumPage";
import { Navigate, useLoaderData, useNavigate, useLocation } from "react-router-dom";
import { Api } from "./Data/Api";
import { useParamIds } from "./Data/Common";
import { InviteParkerReq, Plan } from "./Data/ApiTypes";
import { showMoney } from "./Data/Money";
import { LocalDate } from '@js-joda/core';
import { DateTimeFormatter } from '@js-joda/core';
import { FieldError } from 'react-hook-form';
import { maskAtom } from "./Data/Atoms";
import { useAtom } from "jotai";
import { toast } from "react-toastify";

function getDayWithSuffix(day: number): string {
    if (day >= 11 && day <= 13) return `${day}th`;
    switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
    }
}

type PaymentType = "immediate" | "deferred";

export function FacilityParkerInvitePage() {
    const nav = useNavigate();
    const location = useLocation();
    const plans = useLoaderData() as Plan[];
    const { facilityId } = useParamIds();
    const [msg, setMsg] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [paymentType, setPaymentType] = useState<PaymentType>("immediate");
    const [, setMask] = useAtom(maskAtom);

    const prefillData = location.state as { email?: string; name?: string } | null;

    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<InviteParkerReq>({
        defaultValues: {
            name: prefillData?.name || "",
            emailAddress: prefillData?.email || "",
            phoneNumber: "",
            message: "",
            quantity: 1,
            planId: plans?.[0]?.planId ?? 0,
            planPriceId: plans?.[0]?.prices?.[0]?.planPriceId ?? 0,
            startDate: LocalDate.now()
        }
    });

    const selectedPlanId = watch("planId");
    const prices = plans.find(p => p.planId === selectedPlanId)?.prices || [];
    const selectedPlan = plans.find(p => p.planId === selectedPlanId);
    const invoiceDay = selectedPlan?.dayOfMonthInvoice ?? 1;

    // Reset price when plan changes
    useEffect(() => {
        if (prices.length > 0) {
            setValue("planPriceId", prices[0].planPriceId);
        }
    }, [selectedPlanId, prices, setValue]);

    const vals = watch();

    if (plans.length === 0) {
        return <Navigate to={`/facility/${facilityId}`} />;
    }

    const finalizeSubmit = async (data : InviteParkerReq) => {
        setLoading(true);
        const res = await Api.facilityParkerInvite(facilityId, {
            ...data,
            requirePayment: paymentType === "immediate"
        });
        if (res.isOk) {
            setMsg(res.value);
            toast.success("Invitation sent successfully");
            nav(`/facility/${facilityId}/parker`);
            return;
        }

        setLoading(false);
        toast.error(`There was a problem with the invitation: ${res.error}.`);
        setMsg(res.error);
    }

    return (
        <MediumPage>
             <div className="d-flex align-items-center gap-3">
            <BsButton variant="link" className="p-0" onClick={() => nav(-1)}>  ← Back</BsButton>
            <PageTitle>
                Invite Parker
            
            </PageTitle>
        </div>
         
            <Form onSubmit={handleSubmit(finalizeSubmit)}>
                <Input
                    type="text"
                    label="Parker Name"
                    {...register("name", { required: "Name is required" })}
                    error={errors.name}
                />

                <Input
                    type="email"
                    label="Email"
                    {...register("emailAddress", {
                        required: "Email is required",
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                        }
                    })}
                    error={errors.emailAddress}
                />

                <Input
                    type="tel"
                    label="Phone Number (Optional)"
                    {...register("phoneNumber")}
                    error={errors.phoneNumber}
                />

                <Select label="Plan"
                    {...register("planId", { valueAsNumber: true, required: "Plan is required" })}
                    error={errors.planId}>
                    {plans.map( plan => (
                        <option key={plan.planId} value={plan.planId}>
                            {plan.name}
                        </option>
                    ))}
                </Select>

                <Select
                    label="Price"
                    {...register("planPriceId", {
                        required: "Price is required",
                        valueAsNumber: true } ) }
                    error={errors.planPriceId}>
                    {prices.map( price => (
                        <option key={price.planPriceId}
                                value={price.planPriceId}>
                            {showMoney( price.price )}
                        </option>
                    ))}
                </Select>
                <Alert variant="info" className="mb-3 alert-light-bg">
                    Choose how the parker will be required to pay.
                    <div className="mt-2">
                        <b>Require Payment Immediately</b> — The parker will be prompted to enter payment details at checkout.
                    </div>
                    <div className="mt-2">
                        <b>Allow Deferred Invoicing/Payment</b> — The parker will skip payment for now and receive an invoice on the {getDayWithSuffix(invoiceDay)} of each month.
                        <p className="mt-3" style={{fontSize:".8rem"}}><b>Note:</b> If invoice day has passed, the parker will receive a prorated invoice for the remainder of this month and another for the next.</p>
                    </div>
                </Alert>
                <Select
                    label="Payment Type"
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value as PaymentType)}>
                    <option value="immediate">Require Payment Immediately</option>
                    <option value="deferred">Allow Deferred Invoicing/Payment</option>
                </Select>

                <Input
                    type="date"
                    label="Start Date"
                    {...register("startDate", { 
                        required: "Start date is required",
                        validate: {
                            validDate: (value: any) => {
                                // Skip validation if empty - required will handle this
                                if (!value || value === '') return true;
                                
                                // If it's already a LocalDate, it's valid
                                if (value instanceof LocalDate) return true;
                                
                                // Must be a string in YYYY-MM-DD format
                                if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                    return "Date must be in YYYY-MM-DD format";
                                }

                                try {
                                    const [year, month, day] = value.split('-').map(Number);
                                    const date = LocalDate.of(year, month, day);
                                    const firstOfMonth = LocalDate.now().withDayOfMonth(1);
                                    return date.isAfter(firstOfMonth.minusDays(1)) || "Start date must be on or after the 1st of this month";
                                } catch (e) {
                                    return "Invalid date (e.g., February 30th is not valid)";
                                }
                            }
                        },
                        setValueAs: (value: string | null) => {
                            if (!value) return value;
                            try {
                                const [year, month, day] = value.split('-').map(Number);
                                return LocalDate.of(year, month, day);
                            } catch (e) {
                                return value; // Return original value if parsing fails
                            }
                        }
                    })}
                    min={LocalDate.now().withDayOfMonth(1).format(DateTimeFormatter.ISO_LOCAL_DATE).toString()}
                    error={errors.startDate as FieldError}
                />
                
                <Input
                    type="number"
                    label="Quantity"
                    min="1"
                    {...register("quantity", {
                        required: "Quantity is required",
                        valueAsNumber: true,
                        min: { value: 1, message: "Quantity must be at least 1" },
                    })}
                    error={errors.quantity} />
                <Input
                    type="text"
                    label="Message"
                    {...register("message", { required: "Message is required", 
                        maxLength: { value: 64, message: "Message must be at most 64 characters" }})}
                    error={errors.message}
                />
                <div className="d-flex justify-content-end gap-2 mb-4">
                    <Button type="button" className="w-50" loading={loading}  onClick={() => nav(-1)}>
                        Cancel                     
                    </Button>
                    <Button type="submit" className="w-50 btn-main" loading={loading}>
                        Send Invite
                    </Button>
                </div>
            </Form>
        </MediumPage>
    );
}
